import type { Analysis, NoteEntry } from '@ni/shared';
import { useCreateRun } from '../../api/hooks';
import { analysisOfKind } from '../../lib/analysis-copy';
import { formatDay, plural } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { Shell } from '../run-strip/run-block';

export const UPDATE_BRIEF_LABEL = 'Update the brief';

/**
 * Notes added since the brief was written: what the analyses would read now
 * and did not read then. Confidential notes never reach an analysis, so they
 * never make the brief behind. A long paste stored as pieces counts once.
 */
export function notesSinceBrief(notes: NoteEntry[], analyses: Analysis[]): number {
  const brief = analysisOfKind(analyses, 'person_summary');
  if (!brief) return 0;
  const written = Date.parse(brief.created_at);
  const pastes = new Set(
    notes
      .filter((note) => !note.confidential && Date.parse(note.created_at) > written)
      .map((note) => note.split_group_id ?? note.input_id),
  );
  return pastes.size;
}

/**
 * Whether an owner has edited or removed a note since the brief was written:
 * the brief then reads words the record no longer holds. The server counts
 * only notes research could read (`notes_changed_at`).
 */
export function notesChangedSinceBrief(
  notesChangedAt: string | null,
  analyses: Analysis[],
): boolean {
  const brief = analysisOfKind(analyses, 'person_summary');
  if (!brief || !notesChangedAt) return false;
  return Date.parse(notesChangedAt) > Date.parse(brief.created_at);
}

/** The line's headline: what is new, what changed, or both. */
export function notesSinceBriefLine(count: number, changed: boolean): string {
  const added = count > 0 ? `You've added ${plural(count, 'note', 'notes')}` : null;
  if (added && changed) return `${added} and changed others since this brief was written.`;
  if (added) return `${added} since this brief was written.`;
  return 'A note was edited or deleted since this brief was written.';
}

/**
 * Why the update cannot start yet, or null. Open questions: the server refuses
 * the rewrite on the same rows, and prose written before the answers is prose
 * to write again. A note still being read: its facts are not on the record yet.
 * An edited note whose reading failed: the replaced wording's facts still stand.
 */
export function updateBriefBlocker(openQuestions: number, notes: NoteEntry[]): string | null {
  if (openQuestions > 0)
    return `Answer the ${plural(openQuestions, 'open question', 'open questions')} first. The update will reflect ${openQuestions === 1 ? 'it' : 'them'}.`;
  if (notes.some((note) => !note.confidential && note.extraction_status === 'pending'))
    return 'Your newest note is still being read.';
  /* An edit's old wording comes off the record only once the new wording is
     read, so a brief written now would still rest on what was replaced. */
  if (notes.some((note) => note.edited && note.extraction_status === 'failed'))
    return 'An edited note could not be read. Retry it first.';
  return null;
}

/** The free rewrite (`prose_refresh`): the brief and its readings from the record, no searches. */
export function UpdateBriefButton({
  subjectId,
  blocker,
  onStarted,
  explain = true,
}: {
  subjectId: string;
  blocker: string | null;
  onStarted?: () => void;
  /** Print the blocker above the button; the notes line prints it under its own head. */
  explain?: boolean;
}) {
  const rewrite = useCreateRun(subjectId);
  return (
    <>
      {explain && blocker ? <div className="src">{blocker}</div> : null}
      {rewrite.isError ? <ErrorLine error={rewrite.error} /> : null}
      <WorkingButton
        label={UPDATE_BRIEF_LABEL}
        workingLabel="Updating the brief…"
        working={rewrite.isPending}
        disabled={blocker !== null}
        onClick={() => rewrite.mutate({ kind: 'prose_refresh' }, { onSuccess: onStarted })}
      />
    </>
  );
}

/**
 * The line above the brief when the office has written notes the brief has not
 * read, or changed ones it did.
 */
export function NotesSinceBrief({
  subjectId,
  count,
  changed,
  briefWrittenAt,
  blocker,
}: {
  subjectId: string;
  count: number;
  changed: boolean;
  briefWrittenAt: string;
  blocker: string | null;
}) {
  return (
    <Shell
      tone="quiet"
      label="Notes"
      when={`Brief ${formatDay(briefWrittenAt)}`}
      side={<UpdateBriefButton subjectId={subjectId} blocker={blocker} explain={false} />}
    >
      <p className="rb-head">
        <span className="word">{notesSinceBriefLine(count, changed)}</span>
      </p>
      {blocker ? <p className="rb-note">{blocker}</p> : null}
    </Shell>
  );
}
